import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Loader2, Gift } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState("checking");
  const { clear } = useCart();
  const { customer } = useAuth();

  useEffect(() => {
    if (!sid) { setStatus("error"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setOrder(data); setStatus("paid"); clear(); return; }
        if (tries++ < 15) setTimeout(poll, 2000); else setStatus("pending");
      } catch { setStatus("error"); }
    };
    poll();
    // eslint-disable-next-line
  }, [sid]);

  return (
    <div className="text-white max-w-2xl mx-auto px-6 py-24 text-center">
      {status === "checking" && (<><Loader2 className="mx-auto animate-spin text-[#00B5D8]" size={44} /><p className="mt-6 text-lg">Confirming your payment…</p></>)}
      {status === "paid" && order && (
        <>
          <CheckCircle2 size={56} className="text-[#00B5D8] mx-auto" />
          <h1 className="mt-6 font-display text-5xl">Thank you.</h1>
          <p className="mt-3 text-white/70">Your Preshh GO is on its way.</p>
          {order.order_no && (
            <p className="mt-2 text-sm text-white/60" data-testid="success-order-no">
              Order reference <span className="font-mono font-bold text-[#00B5D8]">{order.order_no}</span>
              {" · "}
              <Link to={`/track?code=${order.order_no}`} className="text-[#00B5D8] hover:underline" data-testid="success-track-link">Track it</Link>
            </p>
          )}
          <div className="mt-8 bg-[#111] border border-white/10 rounded-2xl p-6 text-left">
            <p className="text-sm text-white/60">Order total</p>
            <p className="font-display text-3xl">{money(order.amount, order.currency)}</p>
            <div className="mt-4 border-t border-white/10 pt-4 space-y-2">
              {order.items.map((it, i) => (
                <div key={i} className="flex justify-between text-sm"><span>{it.name} × {it.quantity}</span><span>{money(it.unit_price * it.quantity, order.currency)}</span></div>
              ))}
              {order.shipping_price ? (
                <div className="flex justify-between text-sm text-white/70 pt-2 border-t border-white/5"><span>Shipping ({order.shipping_option || "standard"}{order.shipping_country ? ` · ${order.shipping_country}` : ""})</span><span>{money(order.shipping_price, order.currency)}</span></div>
              ) : null}
              {order.vat_amount ? (
                <div className="flex justify-between text-sm text-white/70" data-testid="success-vat-row">
                  <span>VAT ({Math.round((order.vat_rate || 0) * 100)}%)</span><span>{money(order.vat_amount, order.currency)}</span>
                </div>
              ) : null}
              {order.discount?.amount ? (
                <div className="flex justify-between text-sm text-[#00B5D8]" data-testid="success-discount-row">
                  <span>🎁 {order.discount.type === "birthday_15_accessories" ? "Birthday discount (accessories)" : "Discount"}{order.discount.code ? ` · ${order.discount.code}` : ""}</span>
                  <span>−{money(order.discount.amount, order.currency)}</span>
                </div>
              ) : null}
            </div>
          </div>

          {!customer && (
            <div className="mt-8 bg-gradient-to-br from-[#00B5D8]/15 to-[#00B5D8]/5 border border-[#00B5D8]/40 rounded-3xl p-6 text-left" data-testid="post-purchase-account-prompt">
              <div className="flex gap-3 items-start">
                <Gift size={22} className="text-[#00B5D8] mt-1 shrink-0" />
                <div>
                  <p className="font-display text-xl">Create your Preshh account</p>
                  <p className="mt-2 text-sm text-white/70">Track this order, manage your products, and unlock exclusive customer benefits — including a 15% birthday-month discount on accessories.</p>
                  <Link to={`/register?session_id=${sid}`} className="mt-4 inline-flex preshh-btn-primary text-sm" data-testid="post-purchase-create-account">
                    Create account
                  </Link>
                </div>
              </div>
            </div>
          )}
          {customer && <Link to="/account" className="mt-8 inline-flex preshh-btn-primary">Go to My Account</Link>}
          <Link to="/" className="mt-4 inline-flex preshh-btn-ghost">Back to home</Link>
        </>
      )}
      {status === "pending" && <><p className="text-lg">Your payment is still processing. We'll email you shortly.</p><Link to="/" className="mt-6 inline-flex preshh-btn-ghost">Home</Link></>}
      {status === "error" && <><p className="text-lg">Something went wrong loading your order.</p><Link to="/contact" className="mt-6 inline-flex preshh-btn-primary">Contact support</Link></>}
    </div>
  );
}
